import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { Download, TrendingUp, DollarSign, BarChart3, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PipelineData } from '@/hooks/useAnalytics';

interface PipelineChartProps {
  data: PipelineData[];
  totalValue: number;
  weightedValue: number;
  onExport?: () => void;
  isLoading?: boolean;
}

type ViewMode = 'area' | 'bar';

const stageConfig: Record<string, { label: string; color: string; gradient: [string, string] }> = {
  new: { 
    label: 'New', 
    color: '#6B7280', 
    gradient: ['#9CA3AF', '#6B7280'] 
  },
  qualified: { 
    label: 'Qualified', 
    color: '#3B82F6', 
    gradient: ['#60A5FA', '#3B82F6'] 
  },
  proposal: { 
    label: 'Proposal', 
    color: '#F59E0B', 
    gradient: ['#FBBF24', '#F59E0B'] 
  },
  negotiation: { 
    label: 'Negotiation', 
    color: '#8B5CF6', 
    gradient: ['#A78BFA', '#8B5CF6'] 
  },
  closed_won: { 
    label: 'Closed Won', 
    color: '#10B981', 
    gradient: ['#34D399', '#10B981'] 
  },
  closed_lost: { 
    label: 'Closed Lost', 
    color: '#EF4444', 
    gradient: ['#F87171', '#EF4444'] 
  },
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

export function PipelineChart({
  data,
  totalValue,
  weightedValue,
  onExport,
  isLoading = false,
}: PipelineChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('area');
  const [hoveredStage, setHoveredStage] = useState<string | null>(null);

  const chartData = data.map((item) => ({
    name: stageConfig[item.stage]?.label || item.stage,
    value: item.value,
    count: item.count,
    percentage: item.percentage,
    stage: item.stage,
    color: stageConfig[item.stage]?.color || '#6B7280',
  }));

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ payload: typeof chartData[0] }>; label?: string }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border rounded-xl p-4 shadow-xl"
        >
          <p className="font-semibold text-foreground mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">Value:</span>
              <span className="font-medium text-foreground">{formatCurrency(item.value)}</span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">Deals:</span>
              <span className="font-medium text-foreground">{item.count}</span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">Share:</span>
              <span className="font-medium text-foreground">{item.percentage}%</span>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#2B7FFF]" />
            Revenue Pipeline
          </CardTitle>
          <CardDescription>Pipeline value by stage</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-8 w-8 rounded-md transition-all',
                viewMode === 'area' && 'bg-card shadow-sm'
              )}
              onClick={() => setViewMode('area')}
            >
              <TrendingUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-8 w-8 rounded-md transition-all',
                viewMode === 'bar' && 'bg-card shadow-sm'
              )}
              onClick={() => setViewMode('bar')}
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="icon" onClick={onExport} className="h-9 w-9">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="p-4 rounded-xl bg-gradient-to-br from-[#2B7FFF]/5 to-[#2B7FFF]/10 border border-[#2B7FFF]/20"
          >
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-sm">Total Pipeline</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {isLoading ? (
                <span className="inline-block w-24 h-8 bg-muted rounded animate-pulse" />
              ) : (
                formatCurrency(totalValue)
              )}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="p-4 rounded-xl bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/20"
          >
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">Weighted Value</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {isLoading ? (
                <span className="inline-block w-24 h-8 bg-muted rounded animate-pulse" />
              ) : (
                formatCurrency(weightedValue)
              )}
            </p>
          </motion.div>
        </div>

        {/* Chart */}
        <div className="h-[280px]">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
            </div>
          ) : chartData.length === 0 || chartData.every(d => d.value === 0) ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <TrendingUp className="h-8 w-8 opacity-20" />
              </div>
              <p className="text-lg font-medium">No pipeline data yet</p>
              <p className="text-sm">Add contacts with deal values to see your pipeline</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              {viewMode === 'area' ? (
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pipelineGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2B7FFF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2B7FFF" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#2B7FFF"
                    strokeWidth={3}
                    fill="url(#pipelineGradient)"
                    animationDuration={1500}
                    animationEasing="ease-out"
                  />
                </AreaChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="value"
                    radius={[6, 6, 0, 0]}
                    animationDuration={1500}
                    animationEasing="ease-out"
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        opacity={hoveredStage === null || hoveredStage === entry.stage ? 1 : 0.5}
                        onMouseEnter={() => setHoveredStage(entry.stage)}
                        onMouseLeave={() => setHoveredStage(null)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          )}
        </div>

        {/* Stage Legend */}
        <div className="flex flex-wrap gap-3 justify-center">
          {chartData.length > 0 && chartData.some(d => d.value > 0) && chartData.map((item, index) => (
            <motion.div
              key={item.stage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs cursor-pointer transition-all',
                'bg-muted hover:bg-muted/80',
                hoveredStage === item.stage && 'ring-2 ring-offset-1',
                hoveredStage !== null && hoveredStage !== item.stage && 'opacity-50'
              )}
              style={{ 
                '--ring-color': item.color,
                ringColor: hoveredStage === item.stage ? item.color : undefined 
              } as React.CSSProperties}
              onMouseEnter={() => setHoveredStage(item.stage)}
              onMouseLeave={() => setHoveredStage(null)}
            >
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="font-medium text-foreground">{item.name}</span>
              <span className="text-muted-foreground">({item.count})</span>
            </motion.div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default PipelineChart;
